/* Infinite scroll for the collection grid.
 *
 * The section still renders Shopify's paginator, which is what this replaces:
 * it is the fallback when the script never runs, and it is also where the next
 * page's address comes from. On connect the paginator is hidden and an
 * observer watches this element; each time it comes into view the next page is
 * fetched through the Section Rendering API, its cards are appended, and the
 * address of the page after that is taken from the copy that came back.
 *
 * facets.js replaces the whole of #ProductGridContainer when a filter changes,
 * which destroys this element and builds a fresh one from the server. That is
 * why everything hangs off connectedCallback.
 *
 * Going back from a product: a page Safari or Chrome kept in memory comes back
 * as it was. One they reload only has the first page again, so the browser
 * cannot scroll back to the product. For that case the cards loaded here, the
 * next address and the scroll position are kept in sessionStorage when the
 * shopper leaves, and put back on a back/forward load of the same address.
 */

const MUNNE_GRID_STORAGE_KEY = 'munne:collection-grid';

/* The grid that is on screen: set by the scroller while it exists, and kept
   after it removes itself on the last page, so leaving still saves the cards. */
const munneGrid = { list: null, serverCount: 0, scroller: null };

window.addEventListener('pagehide', () => {
  const { list, serverCount, scroller } = munneGrid;
  if (!list || !list.isConnected) return;
  const loaded = Array.from(list.children).slice(serverCount);
  try {
    window.sessionStorage.setItem(
      MUNNE_GRID_STORAGE_KEY,
      JSON.stringify({
        url: window.location.pathname + window.location.search,
        cards: loaded.map((card) => card.outerHTML).join(''),
        nextUrl: scroller && scroller.isConnected ? scroller.nextUrl : '',
        scrollY: window.scrollY,
      })
    );
  } catch (error) {
    // Storage full or blocked: going back simply starts from the first page.
  }
});

/* A page restored from memory has its cards already, but a fetch cut off when
   the shopper left would have left the scroller waiting: look again. */
window.addEventListener('pageshow', (event) => {
  if (event.persisted && munneGrid.scroller && munneGrid.scroller.isConnected) {
    munneGrid.scroller.loadNext();
  }
});

class MunneInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.list = document.getElementById('product-grid');
    if (!this.list) return;

    this.spinner = this.querySelector('.munne-infinite__spinner');
    this.status = this.querySelector('.munne-infinite__status');
    this.loading = false;

    munneGrid.list = this.list;
    munneGrid.serverCount = this.list.children.length;
    munneGrid.scroller = this;

    // Only now that the script is running does the paginator step aside.
    this.classList.add('is-active');

    if (this.restore()) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      },
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  /* On a back/forward load of the address the shopper left from, put back the
     cards they had loaded and their scroll position. Returns true when the
     last page is already in, so there is nothing left to watch for. */
  restore() {
    const navigation = performance.getEntriesByType('navigation')[0];
    if (!navigation || navigation.type !== 'back_forward') return false;

    let saved;
    try {
      saved = JSON.parse(window.sessionStorage.getItem(MUNNE_GRID_STORAGE_KEY));
    } catch (error) {
      return false;
    }
    if (!saved || saved.url !== window.location.pathname + window.location.search) return false;

    // The browser would scroll before the cards are back, and land short.
    history.scrollRestoration = 'manual';
    this.list.insertAdjacentHTML('beforeend', saved.cards);
    window.scrollTo(0, saved.scrollY);
    // Again once images and fonts are in, in case anything above moved.
    window.addEventListener('load', () => window.scrollTo(0, saved.scrollY), { once: true });

    if (saved.nextUrl) {
      this.dataset.nextUrl = saved.nextUrl;
      return false;
    }
    this.remove();
    return true;
  }

  get nextUrl() {
    return this.dataset.nextUrl || '';
  }

  /* Ask for the section alone rather than the whole page: same markup, a
     fraction of the bytes. */
  sectionUrl(url) {
    const next = new URL(url, window.location.origin);
    next.searchParams.set('section_id', this.dataset.sectionId);
    return next.toString();
  }

  async loadNext() {
    if (this.loading || !this.nextUrl) return;

    this.loading = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.sectionUrl(this.nextUrl));
      if (!response.ok) throw new Error(response.statusText);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = html.getElementById('product-grid');
      const marker = html.querySelector('munne-infinite-scroll');

      if (incoming) {
        const cards = Array.from(incoming.children);
        cards.forEach((card) => this.list.appendChild(card));
        if (this.status) this.status.textContent = `${this.list.children.length} productos`;
      }

      // No marker in the response means that was the last page.
      if (marker && marker.dataset.nextUrl) {
        this.dataset.nextUrl = marker.dataset.nextUrl;
      } else {
        delete this.dataset.nextUrl;
        this.observer.disconnect();
        this.remove();
        return;
      }
    } catch (error) {
      /* Leave the paginator visible so the shopper is not stranded on a page
         that will not grow. */
      this.classList.remove('is-active');
      this.observer.disconnect();
      return;
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('munne-infinite-scroll')) {
  customElements.define('munne-infinite-scroll', MunneInfiniteScroll);
}
